import type { CoinType } from '../cointype/CoinTypeStore';

// The jackpot API speaks CC|SC while the platform coin union is GC|SC — see toJackpotCoin below.
export type JackpotCoinType = 'CC' | 'SC';

export interface JackpotLevel {
  levelUuid: string;
  name: string;
  // Amounts arrive as decimal strings, not numbers — they are money and must not round-trip
  // through a float before we format them.
  //
  // `null` when Splash could not be reached (core's `amountsLive: false`). It is NOT zero and must
  // never be rendered as one: the level exists, its current figure is simply unknown.
  amount: string | null;
}

// GET /api/v1/jackpot/eligibility?coinType=CC|SC — FE_DESIGN_SPEC §7.2.
// The endpoint never 404s: when the player cannot see the jackpot it returns this same envelope
// with `canSee: false` and everything else null/empty, so the lobby has one code path.
export interface JackpotEligibility {
  coinType: JackpotCoinType;
  canSee: boolean;
  canParticipate: boolean;
  participate: boolean;
  jackpotId: string | null;
  /**
   * @deprecated Core's misnamed alias for {@link topLevelAmount} — the same value. Read that instead;
   * this only stays so the app keeps working against a core that predates the rename.
   *
   * OPTIONAL, like its replacement: this is a parsed wire type, and which of the two a given core
   * version sends is exactly what the fallback in `topLevelAmount` exists to absorb. Requiring either
   * would only force every fixture to carry a field the app may never read.
   */
  totalAmount?: string | null;
  /** The TOP LEVEL's pot, by level association priority — the figure every jackpot surface prints. */
  topLevelAmount?: string | null;
  contributionPerRound: string | null;
  levels: JackpotLevel[];
  /**
   * Lobby rows configured as jackpot collections: `master_game_sub_category` ids taken from the
   * CATEGORY + INCLUDE rows of `splash_jackpot.jackpot_eligible_slot_rule` (KRA-303). This is the
   * single source of truth for which row carries the jackpot header — no CMS flag, no name match.
   *
   * Optional because older core builds do not send it; see `isJackpotCategory` for what happens then.
   * An EMPTY array is a real answer (no row is configured), not a missing one.
   */
  eligibleCategoryIds?: number[];
}

/**
 * The optional `jackpot` block on a launch-game response — the in-slot banner's launch-time payload
 * (FE_DESIGN_SPEC §B0, delivered per §6). Composed by player-backend from core's
 * `GET cb/gm/jackpot/launch-context` and forwarded VERBATIM into the game-ui iframe on `gameReady`.
 *
 * The host does not render this and must not reshape it: the lobby widgets have their own
 * `JackpotEligibility` fetch, and game-ui renders the banner from exactly this object. The only
 * decision made here is whether to forward it at all (the feature flag).
 *
 * **Its PRESENCE is the gate.** A launch that does not qualify carries no block, so there is no
 * second boolean to disagree with. `participate` picks between the banner's two designed states
 * (`OPT IN` pill vs `More Info` link) and is NOT a precondition for the block existing.
 */
export interface JackpotLaunchBlock {
  /** JACKPOT coin (`CC`/`SC`) of the resolved group — not the platform coin of the launch. */
  coinType: JackpotCoinType;
  jackpotId: string;
  participate: boolean;
  /** The level the banner itself shows. `levels[0]`; core orders highest tier first. */
  topLevel: JackpotLevel | null;
  /** Every level, so the popup opens without a second round-trip. Never assume four. */
  levels: JackpotLevel[];
  contributionPerRound: string | null;
  /** `false` ⇒ every amount above is `null` because Splash was unreachable. Not zero. */
  amountsLive: boolean;
}

/**
 * `GET /api/v1/jackpot/widget` — core's pacing decision for the lobby widget.
 *
 * The POLICY lives in core (the admin-authored `jackpot_widget_config` rows, the LD segment match,
 * and the display-window / jump-cooldown arithmetic against this player's impression anchor). This
 * side only obeys: render on `show`, auto-open the popup on `jump`.
 */
export interface JackpotWidgetDecision {
  /** JACKPOT coin (`CC`/`SC`) the decision was computed for. */
  coinType: JackpotCoinType;
  /** Whether the widget should be on screen at all. */
  show: boolean;
  /** Whether the widget may open the jackpot popup by itself right now. `jump` implies `show`. */
  jump: boolean;
  /** When the jump cooldown lifts; null when jumping is off or already allowed. */
  nextJumpAt: string | null;
  /** Why — diagnostic only, never branched on here. */
  reason: string;
  /** The targeted segment that decided, or null for ALL targeting and the catch-all row. */
  segmentKey: string | null;
}

/**
 * Platform coin is `GC`/`SC`; the jackpot API's coin is `CC`/`SC`. GC (Crown Coins) is CC on the
 * jackpot side. Mapping this wrong does not error — it silently returns the *other* coin's jackpot,
 * so the lobby would show the SC pot next to the gold coin icon. Mirrors core's
 * `JackpotContributionService.toJackpotCoin`.
 */
export function toJackpotCoin(coinType: CoinType): JackpotCoinType {
  return coinType === 'GC' ? 'CC' : 'SC';
}
